import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Lightning, CheckCircle, XCircle, ArrowsClockwise, Clock,
  Warning, Info, TrendUp,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

export default function XMSyncPanel() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [days, setDays] = useState(1);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/xm/status");
      setStatus(data);
    } catch (e) {
      // silent fail for non-super_admin
    }
  };

  useEffect(() => {
    if (user?.role === "super_admin" || user?.role === "admin_ops") {
      load();
    }
  }, [user]);

  if (!user || (user.role !== "super_admin" && user.role !== "admin_ops")) return null;
  if (!status) return null;

  const runSync = async (endpoint, label) => {
    setSyncing(true);
    try {
      const { data } = await api.post(endpoint);
      setLastResult({ label, data, at: new Date().toISOString() });
      if (data.status === "forbidden") {
        toast.warning(`${label}: 403 Forbidden — broker permission needed`);
      } else if (data.inserted > 0 || data.enriched > 0) {
        toast.success(`${label}: ${data.inserted || data.enriched} new record(s)`);
      } else {
        toast.info(`${label}: no new data`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const configured = status.configured;
  const lastSync = status.last_sync?.timestamp;
  const lastRes = status.last_sync?.result || {};
  const lastClientSync = status.last_client_sync?.timestamp;
  const lastClientRes = status.last_client_sync?.result || {};

  return (
    <div
      data-testid="xm-sync-panel"
      className="bg-gradient-to-br from-[#111827] via-[#0f1a2e] to-[#111827] border border-blue-500/25 rounded-xl p-5 md:p-6 mb-6 relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <Lightning size={22} weight="duotone" className="text-blue-400 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-lg md:text-xl font-bold text-white flex items-center gap-2">
              XM Partners API
              {configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] text-emerald-300 font-mono uppercase tracking-wider">
                  <CheckCircle size={10} weight="fill" /> Configured
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-[10px] text-red-300 font-mono uppercase tracking-wider">
                  <XCircle size={10} weight="fill" /> Not configured
                </span>
              )}
            </h2>
            <p className="text-xs md:text-sm text-gray-400 mt-0.5">
              Pull-based sync from XM&apos;s Trade Statistics API · Auto-registers new loginIds
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 text-xs">
          <div className="p-3 rounded-lg bg-[#0B0F19]/60 border border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Base URL</p>
            <p className="text-white font-mono text-[11px] mt-1 truncate" title={status.base}>{status.base?.replace("https://", "") || "—"}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#0B0F19]/60 border border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Timezone</p>
            <p className="text-white font-mono text-[11px] mt-1">{status.timezone || "UTC"}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#0B0F19]/60 border border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Auto-sync interval</p>
            <p className="text-white font-mono text-[11px] mt-1">
              Trades: {status.sync_interval_minutes}m · Clients: {status.client_sync_interval_minutes || 60}m
            </p>
          </div>
          <div className="p-3 rounded-lg bg-[#0B0F19]/60 border border-white/5">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Last auto-sync</p>
            <p className="text-white font-mono text-[11px] mt-1 flex items-center gap-1">
              <Clock size={10} />
              <span>Trades: {fmtDate(lastSync)}</span>
            </p>
            <p className="text-white font-mono text-[11px] mt-0.5 flex items-center gap-1">
              <Clock size={10} />
              <span>Clients: {fmtDate(lastClientSync)}</span>
            </p>
          </div>
        </div>

        {/* Last sync result */}
        {(lastRes.fetched || lastResult) && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
            {[
              ["Fetched", lastRes.fetched ?? "—", "text-blue-300"],
              ["Inserted", lastRes.inserted ?? "—", "text-emerald-300"],
              ["Duplicates", lastRes.duplicates ?? "—", "text-gray-400"],
              ["New participants", lastRes.auto_registered ?? "—", "text-orange-300"],
              ["Milestones", lastRes.milestones_triggered ?? "—", "text-yellow-300"],
            ].map(([label, val, cls]) => (
              <div key={label} className="p-2 rounded bg-[#0B0F19]/40 border border-white/5 text-center">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
                <p className={`font-mono text-sm mt-0.5 ${cls}`}>{val}</p>
              </div>
            ))}
          </div>
        )}

        {/* Manual controls */}
        <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-white/5 mb-4">
          <label className="text-xs text-gray-400 flex items-center gap-2">
            Pull last
            <select
              data-testid="xm-days-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-[#0B0F19] border border-white/10 rounded px-2 py-1 text-white text-xs"
            >
              <option value={1}>1 day</option>
              <option value={2}>2 days</option>
              <option value={7}>7 days</option>
              <option value={15}>15 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days (max)</option>
            </select>
            of trades
          </label>
          <button
            data-testid="xm-sync-trades-btn"
            onClick={() => runSync(`/xm/sync?days=${days}`, "Trades sync")}
            disabled={syncing || !configured}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
          >
            <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing…" : "Sync Trades"}
          </button>
          <button
            data-testid="xm-sync-clients-btn"
            onClick={() => runSync("/xm/sync-clients?days=30", "Client enrichment")}
            disabled={syncing || !configured}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 text-gray-200 hover:bg-white/5 disabled:opacity-50 text-sm transition-colors"
          >
            <TrendUp size={14} />
            Enrich Clients
          </button>
        </div>

        {/* Info notes */}
        <div className="space-y-2 text-[11px]">
          <div className="flex items-start gap-2 text-gray-400">
            <Info size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p>Rate limit: 80–100 calls/day per endpoint (broker limit). Auto-sync polls every {status.sync_interval_minutes} min = ~{Math.round(1440 / status.sync_interval_minutes)} calls/day (safe).</p>
          </div>
          <div className="flex items-start gap-2 text-gray-400">
            <Info size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p>Times are converted from Cyprus (UTC+2/+3, DST-aware) to UTC before comparison against the campaign window.</p>
          </div>
          {!configured && (
            <div className="flex items-start gap-2 text-red-300 mt-2 p-2 rounded bg-red-500/5 border border-red-500/20">
              <Warning size={12} className="mt-0.5 flex-shrink-0" />
              <p>XM_API_TOKEN is not set in the backend env. Add it and restart the backend to enable sync.</p>
            </div>
          )}
          {lastResult?.data?.status === "forbidden" && (
            <div className="flex items-start gap-2 text-amber-300 mt-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
              <Warning size={12} className="mt-0.5 flex-shrink-0" />
              <p>Trader List API returns 403. Ask broker to enable permission for name/email/phone/clientID enrichment.</p>
            </div>
          )}
          {lastResult?.data?.signup_dates_available > 0 && (
            <div className="flex items-start gap-2 text-emerald-300 mt-2 p-2 rounded bg-emerald-500/5 border border-emerald-500/20">
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <p>
                <strong>signUpDate available:</strong> broker returned{" "}
                {lastResult.data.signup_dates_available} trader records with sign-up dates.
                Participants&apos; <span className="font-mono">eligibility_start_date</span> auto-set to{" "}
                <span className="font-mono">max(campaign_start, signUpDate)</span>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
